import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { projectsApi, categoriesApi } from '../services/api'
import ProjectCard from '../components/ProjectCard'
import { Icon } from '../components/Icons'
import '../styles/Projects.css'

function Projects() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [projects, setProjects] = useState([])
  const [categories, setCategories] = useState([
    { id: 'all', label: 'All Projects', icon: 'folder' }
  ])
  const [allTags, setAllTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get('category') || 'all'
  )
  const [selectedTag, setSelectedTag] = useState(
    searchParams.get('tag') || ''
  )
  const [tagsExpanded, setTagsExpanded] = useState(false)

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesApi.getAll()
        setCategories([
          { id: 'all', label: 'All Projects', icon: 'folder' },
          ...response.data
        ])
      } catch (error) {
        console.error('Error fetching categories:', error)
      }
    }

    fetchCategories()
  }, [])

  // Fetch available tags on mount
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await projectsApi.getTags()
        setAllTags(response.data.tags || [])
      } catch (error) {
        console.error('Error fetching tags:', error)
      }
    }

    fetchTags()
  }, [])

  // Fetch projects when filters change
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true)
      try {
        const filters = {}
        if (selectedCategory !== 'all') {
          filters.category = selectedCategory
        }
        if (selectedTag) {
          filters.tag = selectedTag
        }
        const response = await projectsApi.getAll(filters)
        setProjects(response.data)
      } catch (error) {
        console.error('Error fetching projects:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchProjects()
  }, [selectedCategory, selectedTag])

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId)
    updateSearchParams(categoryId, selectedTag)
  }

  const handleTagChange = (tag) => {
    setSelectedTag(tag)
    updateSearchParams(selectedCategory, tag)
  }

  const updateSearchParams = (category, tag) => {
    const params = {}
    if (category !== 'all') params.category = category
    if (tag) params.tag = tag
    setSearchParams(params)
  }

  const clearFilters = () => {
    setSelectedCategory('all')
    setSelectedTag('')
    setSearchParams({})
  }

  return (
    <div className="projects-page">
      <div className="projects-hero">
        <div className="projects-header">
          <h1>Projects</h1>
        </div>
      </div>

      <div className="projects-content">
        <div className="filters-section">
        <div className="filter-group">
          <h3>Categories</h3>
          <div className="category-filter">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`filter-btn ${
                  selectedCategory === category.id ? 'active' : ''
                }`}
                onClick={() => handleCategoryChange(category.id)}
              >
                <span className="filter-icon"><Icon name={category.icon} size={18} /></span>
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {allTags.length > 0 && (
          <div className="filter-group">
            <div className="tags-header">
              <h3>Tags</h3>
              {allTags.length > 10 && (
                <button
                  className="toggle-tags-btn"
                  onClick={() => setTagsExpanded(!tagsExpanded)}
                >
                  {tagsExpanded ? 'Show Less' : 'Show More'}
                </button>
              )}
            </div>
            <div className={`tag-filter ${tagsExpanded ? 'expanded' : 'collapsed'}`}>
              <button
                className={`tag-btn ${!selectedTag ? 'active' : ''}`}
                onClick={() => handleTagChange('')}
              >
                All Tags
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  className={`tag-btn ${selectedTag === tag ? 'active' : ''}`}
                  onClick={() => handleTagChange(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}

        {(selectedCategory !== 'all' || selectedTag) && (
          <button className="clear-filters-btn" onClick={clearFilters}>
            Clear All Filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading projects...</div>
      ) : projects.length === 0 ? (
        <div className="no-projects">
          <p>No projects found with the selected filters.</p>
          <button className="btn btn-secondary" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      ) : (
        <>
          <div className="results-info">
            Showing {projects.length} project{projects.length !== 1 ? 's' : ''}
            {selectedCategory !== 'all' && ` in ${categories.find(c => c.id === selectedCategory)?.label}`}
            {selectedTag && ` tagged with "${selectedTag}"`}
          </div>
          <div className="projects-grid">
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        </>
      )}
      </div>
    </div>
  )
}

export default Projects
